import type { LanguageAccess, Membership, ProjectPermission } from '@/lib/api';

export type CellValue = 'none' | 'read' | 'write';

export type GridRow = Record<string, CellValue>;

export interface GridState {
  /** userSubject → (langCode → access). Includes only rows we render. */
  rows: Record<string, GridRow>;
  /** Original values from the server, used for dirty detection. */
  baseline: Record<string, GridRow>;
}

export interface GridAction {
  type:
    | 'sync'
    | 'set-cell'
    | 'set-row'
    | 'add-user'
    | 'remove-user'
    | 'reset-row';
  userSubject?: string;
  langCode?: string;
  value?: CellValue;
  rows?: Record<string, GridRow>;
  baseline?: Record<string, GridRow>;
}

/** Reduce a server permission list into a row map keyed by userSubject. */
export function buildRows(
  permissions: ProjectPermission[],
  languages: string[],
): Record<string, GridRow> {
  const rows: Record<string, GridRow> = {};
  for (const p of permissions) {
    const row: GridRow = {};
    for (const code of languages) row[code] = 'none';
    for (const grant of p.languages ?? []) {
      if (languages.includes(grant.code)) {
        row[grant.code] = grant.access === 'write' ? 'write' : 'read';
      }
    }
    rows[p.userSubject] = row;
  }
  return rows;
}

export function emptyRow(languages: string[]): GridRow {
  const row: GridRow = {};
  for (const code of languages) row[code] = 'none';
  return row;
}

/** Identify rows whose current values diverge from the baseline. */
export function dirtyUserSubjects(state: GridState): string[] {
  const subjects = new Set([
    ...Object.keys(state.rows),
    ...Object.keys(state.baseline),
  ]);
  return Array.from(subjects).filter((subj) => !rowEquals(state.rows[subj], state.baseline[subj]));
}

export function isRowDirty(state: GridState, userSubject: string): boolean {
  return !rowEquals(state.rows[userSubject], state.baseline[userSubject]);
}

function rowEquals(a: GridRow | undefined, b: GridRow | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return Object.values(a ?? b ?? {}).every((v) => v === 'none');
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if ((a[k] ?? 'none') !== (b[k] ?? 'none')) return false;
  }
  return true;
}

/** Convert a row to the wire shape expected by PUT /permissions/:userSubject. */
export function toLanguageAccess(row: GridRow): LanguageAccess[] {
  return Object.entries(row)
    .filter(([, v]) => v !== 'none')
    .map(([code, v]) => ({ code, access: v as 'read' | 'write' }));
}

/** A row is empty (= the equivalent of "revoke") when every cell is 'none'. */
export function isRowEmpty(row: GridRow | undefined): boolean {
  if (!row) return true;
  return Object.values(row).every((v) => v === 'none');
}

export function gridReducer(state: GridState, action: GridAction): GridState {
  switch (action.type) {
    case 'sync':
      return {
        rows: action.rows ?? {},
        baseline: action.baseline ?? action.rows ?? {},
      };
    case 'set-cell': {
      const { userSubject, langCode, value } = action;
      if (!userSubject || !langCode || !value) return state;
      const row = state.rows[userSubject] ?? {};
      return {
        ...state,
        rows: { ...state.rows, [userSubject]: { ...row, [langCode]: value } },
      };
    }
    case 'set-row': {
      const { userSubject, value } = action;
      if (!userSubject || !value) return state;
      const row = state.rows[userSubject] ?? {};
      const next: GridRow = {};
      for (const code of Object.keys(row)) next[code] = value;
      return { ...state, rows: { ...state.rows, [userSubject]: next } };
    }
    case 'add-user': {
      const { userSubject } = action;
      if (!userSubject || state.rows[userSubject]) return state;
      const sample = Object.values(state.rows)[0] ?? Object.values(state.baseline)[0];
      const languages = sample ? Object.keys(sample) : [];
      return {
        ...state,
        rows: { ...state.rows, [userSubject]: emptyRow(languages) },
      };
    }
    case 'remove-user': {
      const { userSubject } = action;
      if (!userSubject) return state;
      const rest: GridState['rows'] = { ...state.rows };
      const restBase: GridState['baseline'] = { ...state.baseline };
      delete rest[userSubject];
      delete restBase[userSubject];
      return { rows: rest, baseline: restBase };
    }
    case 'reset-row': {
      const { userSubject } = action;
      if (!userSubject) return state;
      const baselineRow = state.baseline[userSubject];
      const nextRows = { ...state.rows };
      if (baselineRow) nextRows[userSubject] = { ...baselineRow };
      else delete nextRows[userSubject];
      return { ...state, rows: nextRows };
    }
    default:
      return state;
  }
}

/** Membership lookup helper — surfaces displayName/email when present. */
export function memberDisplay(
  subject: string,
  members: Membership[],
): { primary: string; secondary?: string } {
  const m = members.find((x) => x.userSubject === subject);
  if (!m) return { primary: subject };
  return {
    primary: m.displayName || m.email || subject,
    secondary: m.email && m.email !== m.displayName ? m.email : undefined,
  };
}
